import { zodResolver } from '@hookform/resolvers/zod'
import { ClipboardList, Loader2 } from 'lucide-react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { FormSelect } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useSprints } from '../../sprintApi'
import { useSaveTask } from '../../taskApi'
import { todayIso } from '../../taskFilters'
import { PRIORITIES, TASK_STATUSES, TASK_TYPES, type Project, type Task, type TaskInput } from '../../types'
import { toastError, useStaffLookup } from '../../ui'

export type TaskDefaults = Partial<Pick<TaskInput, 'status' | 'type' | 'parentId' | 'installationId' | 'assigneeId'>>

type Props = { open: boolean; project: Project; task: Task | null; defaults?: TaskDefaults; onOpenChange: (v: boolean) => void; onSaved?: (task: Task) => void }

export function TaskFormModal({ open, onOpenChange, ...rest }: Props) {
  return open ? <TaskForm key={rest.task?.id ?? 'new'} {...rest} onDone={() => onOpenChange(false)} /> : null
}

function TaskForm({ project, task, defaults, onDone, onSaved }: Omit<Props, 'open' | 'onOpenChange'> & { onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveTask(project.id)
  const { nameOf } = useStaffLookup()
  const sprints = useSprints(project.id)

  const hours = z.coerce.number<string | number>().min(0, t('projects.tasks.form.hoursInvalid')).max(999, t('projects.tasks.form.hoursInvalid'))
  const schema = z
    .object({
      title: z.string().trim().min(3, t('projects.tasks.form.titleRequired')).max(120),
      description: z.string().max(4000),
      type: z.enum(TASK_TYPES),
      status: z.enum(TASK_STATUSES),
      priority: z.enum(PRIORITIES),
      assigneeId: z.string(),
      reviewerId: z.string(),
      installationId: z.string(),
      parentId: z.string(),
      sprintId: z.string(),
      assignmentReason: z.string().max(1000),
      startDate: z.string().min(1, t('projects.form.dateRequired')),
      dueDate: z.string().min(1, t('projects.form.dateRequired')),
      estimateHours: hours,
      spentHours: hours,
    })
    .refine((v) => !v.startDate || !v.dueDate || v.dueDate >= v.startDate, { path: ['dueDate'], message: t('projects.form.endBeforeStart') })

  const today = todayIso()
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      title: task?.title ?? '',
      description: task?.description ?? '',
      type: task?.type ?? defaults?.type ?? 'development',
      status: task?.status ?? defaults?.status ?? 'todo',
      priority: task?.priority ?? 'medium',
      assigneeId: task?.assigneeId ?? defaults?.assigneeId ?? '',
      reviewerId: task?.reviewerId ?? '',
      installationId: task?.installationId ?? defaults?.installationId ?? '',
      parentId: task?.parentId ?? defaults?.parentId ?? '',
      sprintId: task?.sprintId ?? '',
      assignmentReason: '',
      startDate: task?.startDate ?? today,
      dueDate: task?.dueDate ?? today,
      estimateHours: task?.estimateHours ?? 4,
      spentHours: task?.spentHours ?? 0,
    },
  })
  const { errors } = form.formState
  const startDate = useWatch({ control: form.control, name: 'startDate' })
  const assigneeId = useWatch({ control: form.control, name: 'assigneeId' })
  const assigneeChanged = !!assigneeId && assigneeId !== (task?.assigneeId ?? '')

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      const saved = await save.mutateAsync({
        id: task?.id,
        input: {
          ...v,
          assigneeId: v.assigneeId || null,
          reviewerId: v.reviewerId || null,
          installationId: v.installationId || null,
          parentId: v.parentId || null,
          sprintId: v.sprintId || null,
        },
      })
      toast.success(t(task ? 'projects.tasks.updated' : 'projects.tasks.created', { key: saved.key }))
      if (saved.assignmentRequestId) toast.info(t('projects.assignments.pendingToast'))
      onSaved?.(saved)
      onDone()
    } catch (e) {
      toastError(e)
    }
  })

  const none = { value: '', label: t('common.none') }
  const people = [{ value: '', label: t('projects.work.unassigned') }, ...project.team.map((m) => ({ value: m.userId, label: nameOf(m.userId), description: m.title }))]
  const err = (k: keyof typeof errors) => (errors[k] ? <p className="text-xs text-destructive">{errors[k]?.message}</p> : null)

  return (
    <Modal
      open
      size="xl"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<ClipboardList />}
      title={task ? t('projects.tasks.form.editTitle', { key: task.key }) : t('projects.tasks.form.createTitle')}
      description={t('projects.tasks.form.hint', { project: project.name })}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tk-title">{t('projects.tasks.fields.title')}</Label>
            <Input id="tk-title" aria-invalid={!!errors.title} {...form.register('title')} />
            {err('title')}
          </div>
          <div className="space-y-2">
            <Label htmlFor="tk-desc">{t('projects.tasks.fields.description')}</Label>
            <Textarea id="tk-desc" rows={7} maxLength={4000} {...form.register('description')} />
          </div>
          {assigneeChanged ? (
            <div className="space-y-2">
              <Label htmlFor="tk-reason">{t('projects.assignments.reason')}</Label>
              <Textarea id="tk-reason" rows={2} maxLength={1000} placeholder={t('projects.assignments.reasonHint')} {...form.register('assignmentReason')} />
            </div>
          ) : null}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="tk-inst">{t('projects.tasks.fields.customer')}</Label>
              <FormSelect id="tk-inst" control={form.control} name="installationId" options={[none, ...project.installations.map((i) => ({ value: i.id, label: i.customer, description: t(`projects.installKind.${i.kind}`) }))]} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tk-parent">{t('projects.tasks.fields.parent')}</Label>
              <FormSelect
                id="tk-parent"
                control={form.control}
                name="parentId"
                options={[none, ...project.tasks.filter((x) => x.id !== task?.id && !x.parentId).map((x) => ({ value: x.id, label: `${x.key} · ${x.title}`, keywords: [x.key, x.title] }))]}
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 content-start gap-3 sm:grid-cols-2 md:grid-cols-1">
          <div className="space-y-2">
            <Label htmlFor="tk-type">{t('projects.tasks.fields.type')}</Label>
            <FormSelect id="tk-type" control={form.control} name="type" options={TASK_TYPES.map((x) => ({ value: x, label: t(`projects.taskType.${x}`) }))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tk-status">{t('projects.tasks.fields.status')}</Label>
            <FormSelect id="tk-status" control={form.control} name="status" options={TASK_STATUSES.map((x) => ({ value: x, label: t(`projects.taskStatus.${x}`) }))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tk-priority">{t('projects.tasks.fields.priority')}</Label>
            <FormSelect id="tk-priority" control={form.control} name="priority" options={PRIORITIES.map((x) => ({ value: x, label: t(`projects.priority.${x}`) }))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tk-assignee">{t('projects.tasks.fields.assignee')}</Label>
            <FormSelect id="tk-assignee" control={form.control} name="assigneeId" options={people} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tk-reviewer">{t('projects.tasks.fields.reviewer')}</Label>
            <FormSelect id="tk-reviewer" control={form.control} name="reviewerId" options={people} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tk-sprint">{t('projects.tasks.fields.sprint')}</Label>
            <FormSelect
              id="tk-sprint"
              control={form.control}
              name="sprintId"
              options={[
                { value: '', label: t('projects.sprints.noSprint') },
                ...(sprints.data ?? [])
                  .filter((x) => x.status !== 'closed' || x.id === task?.sprintId)
                  .map((x) => ({ value: x.id, label: x.name, description: t(`projects.sprints.status.${x.status}`) })),
              ]}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tk-start">{t('projects.fields.startDate')}</Label>
            <Controller control={form.control} name="startDate" render={({ field }) => <DateField id="tk-start" value={field.value} onChange={field.onChange} aria-invalid={!!errors.startDate} />} />
            {err('startDate')}
          </div>
          <div className="space-y-2">
            <Label htmlFor="tk-due">{t('projects.tasks.fields.dueDate')}</Label>
            <Controller control={form.control} name="dueDate" render={({ field }) => <DateField id="tk-due" value={field.value} onChange={field.onChange} min={startDate || undefined} aria-invalid={!!errors.dueDate} />} />
            {err('dueDate')}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:col-span-2 md:col-span-1">
            <div className="space-y-2">
              <Label htmlFor="tk-est">{t('projects.tasks.fields.estimate')}</Label>
              <Input id="tk-est" type="number" min={0} step={0.5} inputMode="decimal" {...form.register('estimateHours')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tk-spent">{t('projects.tasks.fields.spent')}</Label>
              <Input id="tk-spent" type="number" min={0} step={0.5} inputMode="decimal" {...form.register('spentHours')} />
            </div>
          </div>
          {err('estimateHours') ?? err('spentHours')}
        </div>
      </div>
    </Modal>
  )
}
